import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import * as Avatar from '@givebutter/primitives/avatar';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { useMediaQuery } from '@givebutter/react-util';
import { maskDecimal } from '@givebutter/util';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import {
    faChevronLeft,
    faChevronRight,
    faEnvelope,
    faPhone,
    faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteFirstTimeSupporter, getFirstTimeSupporters } from 'api/contacts/contacts';
import * as AddEvent from 'assets/js/pages/contacts/single/timeline/add-event';
import { CopyButton } from 'components/copy-button';
import * as Form from 'components/form';
import { TruncatedText } from 'components/truncated-text';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { AnimatePresence, motion } from 'framer-motion';
import { rawDateFromServer } from 'helpers/dates';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twMerge } from 'tailwind-merge';
import { FirstTimeSupporter } from 'types/domain';

const FirstTimeSupporters = () => {
    const toast = useToast();
    const { timezone, accountId, emailConfig } = useCurrentAccount();
    const queryClient = useQueryClient();
    const isMobile = useMediaQuery(`(max-width: 640px)`);
    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.contactsFirstTimeSupporters(accountId),
        queryFn: () => getFirstTimeSupporters(accountId, { page: 1, limit: 50 }),
        select: data => {
            return { contacts: data.data, meta: data.meta };
        },
    });
    const [selectedContactIndex, setSelectedContactIndex] = useState(isMobile ? -1 : 0);
    const selectedContact = data?.contacts[selectedContactIndex];
    const hasFirstTimeSupporters = data?.contacts && data.contacts.length > 0;
    const isLastContact = data?.contacts && selectedContactIndex === data?.contacts.length - 1;
    const isFirstContact = selectedContactIndex === 0;

    const gotoNextTask = () => {
        if (!isLastContact) {
            setSelectedContactIndex(selectedContactIndex + 1);
        }
    };

    const gotoPreviousTask = () => {
        if (!isFirstContact) {
            setSelectedContactIndex(selectedContactIndex - 1);
        }
    };

    const selectNextContact = () => {
        if (data?.contacts.length && selectedContactIndex === data.contacts.length - 1) {
            setSelectedContactIndex(0);
        }
    };

    return (
        <AnimatePresence>
            <div
                className={`w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm sm:flex ${
                    hasFirstTimeSupporters ? 'h-[27.5rem]' : 'flex-col'
                }`}
            >
                {((selectedContact === undefined && isMobile) || !isMobile) && (
                    <motion.div
                        key={'supporter-list'}
                        initial={{ x: -500 }}
                        animate={{ x: 0 }}
                        exit={{ x: 500 }}
                        transition={{ type: 'tween' }}
                        className={`${hasFirstTimeSupporters ? 'sm:w-2/4 sm:border-r' : ''}`}
                    >
                        <div className={`border-b border-gray-200 p-6`}>
                            <div className="flex gap-2">
                                <div className="text-lg font-medium">New supporters</div>
                                {hasFirstTimeSupporters && (
                                    <span className="bg-blue-25 flex items-center justify-center whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium text-blue-500">
                                        {data.contacts.length} contact
                                        {data.contacts.length > 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                            <div className="text-sm text-gray-600">
                                Encourage first time donors to stay involved!
                            </div>
                        </div>
                        <div className="max-h-[21.5rem] overflow-y-auto sm:max-h-full">
                            {hasFirstTimeSupporters && (
                                <>
                                    {data.contacts.map(
                                        (contact: FirstTimeSupporter, index: number) => {
                                            return (
                                                <Button
                                                    variant="transparent"
                                                    color="neutral"
                                                    size="md"
                                                    onClick={() => {
                                                        setSelectedContactIndex(index);
                                                    }}
                                                    key={contact.id}
                                                    className={twMerge(
                                                        'h-fit max-h-[4.5rem] w-full justify-between border-b border-gray-200 px-6 py-4 font-medium last-of-type:border-none hover:bg-gray-50',
                                                        selectedContactIndex === index &&
                                                            'bg-gray-50',
                                                    )}
                                                >
                                                    <div className="flex flex-col items-start">
                                                        <div>
                                                            {contact.type === ContactType.Company
                                                                ? contact.company_name
                                                                : contact.name_display}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            ${maskDecimal(contact.amount)}{' '}
                                                            {rawDateFromServer(
                                                                contact.transacted_at,
                                                            )
                                                                .setZone(timezone)
                                                                .toLocaleString(DateTime.DATE_MED)}
                                                        </div>
                                                    </div>
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        className="text-gray-300"
                                                    />
                                                </Button>
                                            );
                                        },
                                    )}
                                    <div className="p-4 text-center text-sm text-gray-500 sm:mb-24">
                                        End of list
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
                {!!selectedContact && (
                    <motion.div
                        key={'supporter-info'}
                        initial={{ x: 500 }}
                        animate={{ x: 0 }}
                        exit={{ x: -500 }}
                        transition={{ type: 'tween' }}
                        className={`relative h-full sm:w-2/4`}
                    >
                        <div className="p-6" key={selectedContact.id}>
                            <div className="mb-4 text-sm font-medium text-blue-500 sm:hidden">
                                <Button
                                    variant="none"
                                    color="primary"
                                    className="font-medium"
                                    onClick={() => {
                                        setSelectedContactIndex(-1);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} /> Back
                                </Button>
                            </div>
                            <div className="mb-2 flex justify-between">
                                <div className="text-lg font-medium">
                                    <Link
                                        to={`/accounts/${accountId}/contacts/${selectedContact.id}`}
                                        className="text-gray-900"
                                    >
                                        {selectedContact.type === ContactType.Company
                                            ? selectedContact.company_name
                                            : `${selectedContact.first_name} ${selectedContact.last_name}`}
                                    </Link>
                                </div>
                                <div className="mb-4 flex gap-4">
                                    <Button
                                        variant="none"
                                        color="neutral"
                                        disabled={isFirstContact}
                                        className="text-gray-400 hover:text-gray-500 disabled:text-gray-300"
                                        onClick={() => {
                                            gotoPreviousTask();
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                                    </Button>
                                    <Button
                                        variant="none"
                                        color="neutral"
                                        disabled={isLastContact}
                                        className="text-gray-400 hover:text-gray-500 disabled:text-gray-300"
                                        onClick={() => {
                                            gotoNextTask();
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} size="lg" />
                                    </Button>
                                </div>
                            </div>
                            <div className="mb-6 flex justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Amount</p>
                                    <p>${maskDecimal(selectedContact.amount)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">
                                        Date{' '}
                                        <span className="text-xs">
                                            ({DateTime.now().setZone(timezone).offsetNameShort})
                                        </span>
                                    </p>
                                    <p>
                                        {rawDateFromServer(selectedContact.transacted_at)
                                            .setZone(timezone)
                                            .toLocaleString(DateTime.DATE_MED)}
                                    </p>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-600">Reference #</div>
                                    <div>
                                        <Link
                                            to={`/accounts/${accountId}/transactions/${selectedContact.transaction_id}`}
                                            className="font-normal text-gray-600"
                                        >
                                            {selectedContact.transaction_number}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-6">
                                <div className="text-sm text-gray-600">Campaign</div>
                                <div>{selectedContact.campaign_title}</div>
                            </div>
                            {!!selectedContact.primary_email && (
                                <div className="mb-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm text-gray-600">Email</div>
                                            <div className="flex w-[150px] items-center sm:w-[250px]">
                                                <TruncatedText>
                                                    <span>{selectedContact.primary_email}</span>
                                                </TruncatedText>
                                                <CopyButton
                                                    className="text-gray-600"
                                                    text={selectedContact.primary_email}
                                                />
                                            </div>
                                        </div>
                                        <a
                                            href={`mailto:${selectedContact.primary_email}?bcc=${emailConfig.bccAddress}`}
                                        >
                                            <Avatar.Root className={'!h-9 !w-9 !bg-blue-50'}>
                                                <Avatar.Fallback
                                                    aria-label={`Send Email to ${selectedContact.primary_email} with BCC: ${emailConfig.bccAddress}`}
                                                >
                                                    <FontAwesomeIcon
                                                        className={'text-blue-500'}
                                                        icon={faEnvelope}
                                                    />
                                                </Avatar.Fallback>
                                            </Avatar.Root>
                                        </a>
                                    </div>
                                </div>
                            )}

                            {!!selectedContact.primary_phone && (
                                <div className="mb-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm text-gray-600">Phone</div>
                                            <div className="flex items-center">
                                                {getFormattedPhoneNumber(
                                                    selectedContact.primary_phone,
                                                )}
                                                <CopyButton
                                                    className="text-gray-600"
                                                    text={getFormattedPhoneNumber(
                                                        selectedContact.primary_phone,
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <a href={`tel:${selectedContact.primary_phone}`}>
                                            <Avatar.Root className={'!h-9 !w-9 !bg-blue-50'}>
                                                <Avatar.Fallback
                                                    aria-label={`Call ${selectedContact.primary_phone}`}
                                                >
                                                    <FontAwesomeIcon
                                                        className={'text-blue-500'}
                                                        icon={faPhone}
                                                    />
                                                </Avatar.Fallback>
                                            </Avatar.Root>
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between border-t border-gray-200 bg-gray-50 p-4">
                            <AcknowledgeFirstTimeSupporter
                                selectedDonorTask={selectedContact}
                                onSuccess={() => {
                                    queryClient.invalidateQueries({
                                        queryKey:
                                            accountQueryKeys.contactsFirstTimeSupporters(accountId),
                                    });
                                    toast.success(<>Successfully acknowledged donor.</>);
                                    selectNextContact();
                                }}
                            />
                            <AddEvent.Root
                                contact={selectedContact}
                                isFirstTimeSupporterList
                                onSuccess={selectNextContact}
                            >
                                <AddEvent.Trigger asChild>
                                    <Button variant="solid" size="sm">
                                        <FontAwesomeIcon icon={faPlus} /> Add Event
                                    </Button>
                                </AddEvent.Trigger>
                            </AddEvent.Root>
                        </div>
                    </motion.div>
                )}
                {!hasFirstTimeSupporters && (
                    <div className="flex flex-grow flex-col items-center justify-center p-6 text-center">
                        <Avatar.Root className={'!h-9 !w-9 !bg-gray-100'}>
                            <Avatar.Fallback aria-label={'No supporters icon'}>
                                <FontAwesomeIcon icon={faUsers} className="text-gray-500" />
                            </Avatar.Fallback>
                        </Avatar.Root>
                        <div className="text-sm font-medium text-gray-700">No new supporters</div>
                        <div className="text-sm">
                            Reach out to your donors and encourage them to support.
                        </div>
                    </div>
                )}
            </div>
        </AnimatePresence>
    );
};

const AcknowledgeFirstTimeSupporter = (props: {
    selectedDonorTask: FirstTimeSupporter;
    onSuccess?: Function;
}) => {
    const { selectedDonorTask, onSuccess } = props;
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const methods = Form.useGivebutterForm();
    const { handleSubmit, resetFormButton } = methods;
    const { mutate } = useMutation({
        mutationFn: () => deleteFirstTimeSupporter(accountId, selectedDonorTask?.id as number),
        onSuccess: () => {
            onSuccess?.();
        },
        onError: () => {
            toast.error(<>There was a problem acknowledging this donor. Please try again.</>);
            resetFormButton();
        },
    });

    return (
        <Form.GivebutterFormProvider {...methods}>
            <form onSubmit={handleSubmit(() => mutate())}>
                <Form.SubmitButton size="sm" className="!border-gray-300 !bg-white !text-black">
                    Acknowledge
                </Form.SubmitButton>
            </form>
        </Form.GivebutterFormProvider>
    );
};

export { FirstTimeSupporters };
